import { api } from '@/lib/api';

// Backend response interface
interface BackendSubscriptionPlan {
  id: string;
  planType: string;
  price: number;
  currency?: string;
  billingCycle: string; // "monthly" | "yearly"
  maxInvitations?: number;
  features: string[];
  trialDays?: number | null;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  pricing: number;
  validity: number; // Duration in days
  features: string[];
  maxInvitations?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubscriptionPlanData {
  name: string;
  pricing: number;
  validity: number;
  features: string[];
  maxInvitations?: number;
  isActive?: boolean;
}

export interface UpdateSubscriptionPlanData {
  name?: string;
  pricing?: number;
  validity?: number;
  features?: string[];
  maxInvitations?: number;
  isActive?: boolean;
}

// Helper function to convert planType to display name
function getPlanDisplayName(planType: string): string {
  const nameMap: Record<string, string> = {
    trial: 'Free Trial',
    basic: 'Basic',
    pro: 'Pro',
  };
  return nameMap[planType.toLowerCase()] || planType.charAt(0).toUpperCase() + planType.slice(1);
}

// Helper function to convert billingCycle to validity in days
function billingCycleToDays(billingCycle: string): number {
  const cycleMap: Record<string, number> = {
    monthly: 30,
    yearly: 365,
  };
  return cycleMap[billingCycle.toLowerCase()] || 30;
}

// Transform backend response to frontend format
function transformPlan(backendPlan: BackendSubscriptionPlan): SubscriptionPlan {
  // Use description if available (custom plan name), otherwise use mapped name from planType
  const displayName = backendPlan.description?.trim() || getPlanDisplayName(backendPlan.planType);
  
  return {
    id: backendPlan.id,
    name: displayName,
    pricing: backendPlan.price,
    validity: billingCycleToDays(backendPlan.billingCycle),
    features: backendPlan.features,
    maxInvitations: backendPlan.maxInvitations,
    isActive: backendPlan.isActive,
    createdAt: backendPlan.createdAt,
    updatedAt: backendPlan.updatedAt,
  };
}

// Helper function to generate a unique planType from plan name
function generatePlanTypeFromName(name: string): string {
  // First check for exact matches to standard plan types
  const nameToPlanType: Record<string, string> = {
    'Free Trial': 'trial',
    'Basic': 'basic',
    'Pro': 'pro',
    'trial': 'trial',
    'basic': 'basic',
    'pro': 'pro',
  };
  
  const normalizedName = name?.trim() || '';
  const exactMatch = nameToPlanType[normalizedName] || nameToPlanType[normalizedName.toLowerCase()];
  
  if (exactMatch) {
    return exactMatch;
  }
  
  // Generate a unique slug from the plan name
  // Convert to lowercase, replace spaces/special chars with hyphens, remove duplicates
  const slug = normalizedName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
    .substring(0, 50); // Limit length
  
  // Ensure it's not empty
  return slug || 'plan-' + Date.now().toString().slice(-6);
}

// Transform frontend format to backend format for create
function transformCreateToBackendFormat(data: CreateSubscriptionPlanData): any {
  // Generate unique planType from name
  const planType = generatePlanTypeFromName(data.name);
  
  // Determine billing cycle from validity
  const billingCycle = data.validity && data.validity >= 365 ? 'yearly' : 'monthly';

  // Use user-provided maxInvitations if available, otherwise use defaults based on plan type
  const defaultMaxInvitations: Record<string, number> = {
    trial: 25,
    basic: 15,
    pro: 25,
  };

  const backendData: any = {
    planType,
    price: data.pricing,
    billingCycle,
    features: data.features,
    maxInvitations: data.maxInvitations !== undefined ? data.maxInvitations : (defaultMaxInvitations[planType] || 5),
    description: data.name, // Store the custom plan name in description
  };

  // Add optional fields
  if (data.isActive !== undefined) {
    backendData.isActive = data.isActive;
  }

  // Add trialDays for trial plan
  if (planType === 'trial' && data.validity) {
    backendData.trialDays = data.validity;
  }

  return backendData;
}

// Transform frontend format to backend format for update
function transformUpdateToBackendFormat(data: UpdateSubscriptionPlanData): any {
  const backendData: any = {};

  if (data.pricing !== undefined) {
    backendData.price = data.pricing;
  }

  if (data.validity !== undefined) {
    backendData.billingCycle = data.validity >= 365 ? 'yearly' : 'monthly';
  }

  if (data.features !== undefined) {
    backendData.features = data.features;
  }

  if (data.isActive !== undefined) {
    backendData.isActive = data.isActive;
  }

  // Update description (plan name) if name is provided
  if (data.name !== undefined) {
    backendData.description = data.name;
  }

  // Update maxInvitations if provided
  if (data.maxInvitations !== undefined) {
    backendData.maxInvitations = data.maxInvitations;
  }

  return backendData;
}

export const subscriptionPlanService = {
  async getAll(): Promise<SubscriptionPlan[]> {
    const response = await api.get<BackendSubscriptionPlan[]>('/subscription-plans');
    return response.data.map(transformPlan);
  },

  async getOne(id: string): Promise<SubscriptionPlan> {
    const response = await api.get<BackendSubscriptionPlan>(`/subscription-plans/${id}`);
    return transformPlan(response.data);
  },

  async create(data: CreateSubscriptionPlanData): Promise<SubscriptionPlan> {
    const backendData = transformCreateToBackendFormat(data);
    const response = await api.post<BackendSubscriptionPlan>('/subscription-plans', backendData);
    return transformPlan(response.data);
  },

  async update(id: string, data: UpdateSubscriptionPlanData): Promise<SubscriptionPlan> {
    const backendData = transformUpdateToBackendFormat(data);
    const response = await api.put<BackendSubscriptionPlan>(`/subscription-plans/${id}`, backendData);
    return transformPlan(response.data);
  },

  async toggleActivation(id: string): Promise<SubscriptionPlan> {
    const response = await api.patch<BackendSubscriptionPlan>(`/subscription-plans/${id}/toggle-activation`);
    return transformPlan(response.data);
  },
};




